import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Field, Fieldset, Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { RadioGroup } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	FireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import {
	type CustomerAccess,
	CUSTOMER_ACCESS,
	ACCESS_TYPES,
	SHARE_REQUESTS_ACCESS,
	type PortalAccessEnum,
	type ShareRequestAccessEnum,
} from '../../../common/types';
import { CustomerAccessDropdown } from '../../../common/ui/customer-access-dropdown';
import type { SecuritySettings } from '../../../services/types';
import { useShareRequestsOptions } from './questions';
import {
	WhoCanAccessTip,
	WhoCanAccessDescription,
	OpenAccessTip,
	CustomerOrgSharingTip,
} from './questions/main';
import messages from './questions/messages';

export type Props = {
	isProjectSimplified: boolean;
	projectKey: string;
	settings: SecuritySettings;
	portalAccess: PortalAccessEnum;
	shareRequestsAccess: ShareRequestAccessEnum;
	onPortalAccessChange: (value: PortalAccessEnum) => void;
	onShareRequestAccessChange: (value: ShareRequestAccessEnum) => void;
};

export const FormContent = ({
	portalAccess,
	onPortalAccessChange,
	shareRequestsAccess,
	onShareRequestAccessChange,
	settings,
	isProjectSimplified,
}: Props) => {
	const { projectName, serviceDeskEmail, portalUrl } = settings;
	const isAdmin = useIsAdmin();

	const { formatMessage } = useIntl();

	const shareRequestOptions = useShareRequestsOptions();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isCustomerAbleToAddNewCustomers =
		(portalAccess === ACCESS_TYPES.PUBLIC || portalAccess === ACCESS_TYPES.ANONYMOUS) &&
		shareRequestsAccess !== SHARE_REQUESTS_ACCESS.AGENT_ONLY;

	const onShareRequestAccessUpdate = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type.
		({ target: { value } }) => {
			onShareRequestAccessChange(value);
		},
		[onShareRequestAccessChange],
	);

	const createLearnMoreLink = () => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const fireUIAnalyticsEvent = (event: any, analyticsEvent: any) => {
			fireUIAnalytics(analyticsEvent, 'clickOnUsersAndRolesLearnMoreButton');
		};
		const fireUIAnalyticsEventForIPH = (articleId: string) => {
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'helpLink clicked',
				'clickOnUsersAndRolesLearnMoreButton',
				{ articleId },
			);
		};
		const learnMoreLinkButton = (
			<Button
				spacing="none"
				appearance="link"
				href="https://support.atlassian.com/jira-service-management-cloud/docs/what-are-user-types-and-roles/"
				onClick={fireUIAnalyticsEvent}
				target="_blank"
				rel="nofollow noreferrer noopener"
			>
				{formatMessage(messages.learnMoreLinkJsm)}
			</Button>
		);
		const learnMoreArticleIdForTeamProject = 'LDYDgbXqSddD8dC4pAUWi';
		const learnMoreArticleIdForClassicProject = '6aIMqm2F5KAhHo3BbFYp0p';
		const articleId = isProjectSimplified
			? learnMoreArticleIdForTeamProject
			: learnMoreArticleIdForClassicProject;
		return (
			<HelpPanelLink
				articleId={articleId}
				appearance="link"
				spacing="none"
				fallbackComponent={learnMoreLinkButton}
				onClick={() => fireUIAnalyticsEventForIPH(articleId)}
			>
				{formatMessage(messages.learnMoreLinkJsm)}
			</HelpPanelLink>
		);
	};

	const usersAndRolesLearnMoreLink = createLearnMoreLink();

	const onCustomerAccessChangeUpdate = useCallback(
		(customerAccess: CustomerAccess) => {
			onPortalAccessChange(
				customerAccess === CUSTOMER_ACCESS.RESTRICTED
					? ACCESS_TYPES.RESTRICTED
					: ACCESS_TYPES.PUBLIC,
			);
		},
		[onPortalAccessChange],
	);

	const customerSharingDisplayText = formatMessage(messages.whoCanCustomersShareRequest);

	return (
		<>
			<SectionMessage appearance="information">
				<Text as="p">
					<FormattedMessage
						{...messages.newCustomersByEmailInfo}
						values={{ usersAndRolesLearnMoreLink }}
					/>
				</Text>
			</SectionMessage>
			<FieldContainer>
				<Field name="who-can-access-the-portal">
					{() => (
						<>
							<Box xcss={headingContainerStyles}>
								<Heading size="large">
									<WhoCanAccessTip />
								</Heading>
							</Box>
							<Label htmlFor="CustomerAccessDropdownType">
								<WhoCanAccessDescription
									projectName={projectName}
									portalLink={portalUrl}
									serviceDeskEmail={serviceDeskEmail}
								/>
							</Label>
							<DropdownWrapper>
								<CustomerAccessDropdown
									initialValue={portalAccess}
									onCustomerAccessChange={onCustomerAccessChangeUpdate}
								/>
							</DropdownWrapper>
						</>
					)}
				</Field>
				<p>
					<OpenAccessTip />
				</p>
			</FieldContainer>
			<FieldContainer>
				<Field name="who-can-customers-share-requests">
					{({ fieldProps }) => (
						<>
							<Heading size="large">{customerSharingDisplayText}</Heading>
							<p>{formatMessage(messages.whoCanCustomersShareRequestDescription)}</p>
							<Fieldset legend={<VisuallyHidden>{customerSharingDisplayText}</VisuallyHidden>}>
								<RadioGroup
									{...fieldProps}
									options={shareRequestOptions}
									value={shareRequestsAccess}
									onChange={onShareRequestAccessUpdate}
								/>
							</Fieldset>
						</>
					)}
				</Field>
				<p>{isAdmin && <CustomerOrgSharingTip />}</p>
			</FieldContainer>
			{isCustomerAbleToAddNewCustomers && (
				<SectionMessage appearance="warning">
					<p>{formatMessage(messages.newCustomerWarning)}</p>
					<ul>
						<li>{formatMessage(messages.newCustomerViaPortal)}</li>
						<li>{formatMessage(messages.newCustomerViaEmail)}</li>
					</ul>
				</SectionMessage>
			)}
			<FireTrackAnalytics eventName="customerPermissionsSettings viewed" />
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldContainer = styled.div({
	marginBottom: token('space.300', '24px'),
	marginTop: token('space.500', '40px'),
});

const headingContainerStyles = xcss({
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${5 * gridSize}px`,
	width: '480px',
	marginBottom: token('space.100', '8px'),
});
